/**
 * Utility functions for Redux thunks
 */

import { createAsyncThunk } from '@reduxjs/toolkit';

// Global registry to track pending requests
const pendingRequestsRegistry = {};

/**
 * Creates a request key based on thunk name and parameters
 * 
 * @param {string} thunkPrefix - The prefix for the thunk action type
 * @param {any} params - The parameters passed to the thunk
 * @returns {string} A unique key for the request
 */
export const createRequestKey = (thunkPrefix, params) => {
  return `${thunkPrefix}:${JSON.stringify(params)}`;
};

/**
 * Creates a condition function that prevents duplicate thunk calls with the same parameters
 * 
 * @param {string} thunkPrefix - The prefix for the thunk action type
 * @returns {Function} A condition function to be used with createAsyncThunk
 */
export const createDuplicateRequestFilter = (thunkPrefix) => {
  return (params, { getState }) => {
    const requestKey = createRequestKey(thunkPrefix, params);
    
    // If there's already a pending request with these exact parameters, skip this one
    if (pendingRequestsRegistry[requestKey]) {
      return false;
    }
    
    // Mark this request as pending
    pendingRequestsRegistry[requestKey] = true;
    
    return true;
  };
};

/**
 * Clears the pending flag for a request when it completes
 * 
 * @param {string} thunkPrefix - The prefix for the thunk action type
 * @param {any} params - The parameters that were passed to the thunk
 */
export const clearPendingRequest = (thunkPrefix, params) => {
  const requestKey = createRequestKey(thunkPrefix, params);
  delete pendingRequestsRegistry[requestKey];
};

/**
 * Creates extraReducers that handle clearing pending request flags
 * Use this to add automatic cleanup to your slice's extraReducers
 * 
 * @param {object} builder - The builder object from createSlice's extraReducers
 * @param {object} thunkAction - The thunk action creator
 * @param {string} thunkPrefix - The prefix for the thunk action type
 * @returns {object} The builder with added cases
 */
export const addPendingRequestCleanup = (builder, thunkAction, thunkPrefix) => {
  return builder
    .addCase(thunkAction.fulfilled, (state, action) => {
      clearPendingRequest(thunkPrefix, action.meta.arg);
    })
    .addCase(thunkAction.rejected, (state, action) => {
      clearPendingRequest(thunkPrefix, action.meta.arg);
    });
};

/**
 * Creates an enhanced async thunk that automatically prevents duplicate requests
 * This is a wrapper around createAsyncThunk that adds deduplication functionality
 * 
 * @param {string} typePrefix - The action type prefix
 * @param {Function} payloadCreator - The async function that returns the payload
 * @param {object} options - Additional options for createAsyncThunk
 * @returns {AsyncThunk} A Redux async thunk action creator
 */
export const createDedupedAsyncThunk = (typePrefix, payloadCreator, options = {}) => {
  const enhancedOptions = {
    ...options,
    condition: options.condition 
      ? (arg, api) => {
          // First check our duplicate filter, then the user's condition if provided
          const notDuplicate = createDuplicateRequestFilter(typePrefix)(arg, api);
          return notDuplicate ? (options.condition(arg, api) ?? true) : false;
        }
      : createDuplicateRequestFilter(typePrefix)
  };

  const thunk = createAsyncThunk(typePrefix, payloadCreator, enhancedOptions);

  // Add a reference to the original thunk prefix to make cleanup easier
  thunk.typePrefix = typePrefix;

  return thunk;
};
